import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchCustomerActivity, type CustomerActivityItem } from '../services/customerActivityApi';
import { useCustomerCredential } from '../hooks/useCustomerCredential';
import { PaymentService } from '../services/paymentService';
import { logger } from '@core/logger';
import { Drawer } from '../../../../packages/ui/src';

function formatWhen(iso: string, lng: string): string {
  try {
    const d = new Date(iso);
    const loc = ['es', 'en', 'fr', 'de'].includes(lng) ? lng : 'en';
    return d.toLocaleString(loc, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatSlot(iso: unknown, lng: string): string {
  if (!iso || typeof iso !== 'string') return '';
  try {
    const loc = ['es', 'en', 'fr', 'de'].includes(lng) ? lng : 'en';
    return new Date(iso).toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function ActivityRescheduleProposalBlock({
  tenantId,
  email,
  item,
  lng,
  onRefresh,
}: {
  tenantId: string;
  email: string;
  item: CustomerActivityItem;
  lng: string;
  onRefresh: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  if (item.eventType !== 'booking.reschedule_proposed') return null;
  const payload = item.payload || {};
  const bookingId = typeof payload.bookingId === 'string' ? payload.bookingId : '';
  if (!bookingId) return null;

  const run = async (kind: 'accept' | 'reject') => {
    setBusy(kind);
    setMsg(null);
    try {
      const tokens = await PaymentService.mintRescheduleTokens(tenantId, bookingId, email);
      if (kind === 'accept') {
        await PaymentService.acceptRescheduleByToken(tokens.acceptToken);
      } else {
        await PaymentService.rejectRescheduleByToken(tokens.rejectToken);
      }
      setMsg(t('booking.activity_reschedule_done'));
      await onRefresh();
    } catch (e) {
      logger.warn('reschedule action failed', e);
      setMsg(t('booking.activity_reschedule_error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="booking-activity-reschedule">
      <div className="booking-activity-reschedule-times">
        <div>
          <strong>{t('booking.activity_reschedule_current')}</strong> {formatSlot(payload.oldStartTime, lng)}
        </div>
        <div>
          <strong>{t('booking.activity_reschedule_proposed')}</strong> {formatSlot(payload.proposedStartTime, lng)}
        </div>
      </div>
      <div className="booking-activity-reschedule-actions">
        <button
          type="button"
          className="booking-activity-btn booking-activity-btn--primary"
          disabled={!!busy}
          onClick={() => void run('accept')}
        >
          {busy === 'accept' ? t('booking.loading') : t('booking.activity_reschedule_accept')}
        </button>
        <button type="button" className="booking-activity-btn" disabled={!!busy} onClick={() => void run('reject')}>
          {busy === 'reject' ? t('booking.loading') : t('booking.activity_reschedule_reject')}
        </button>
      </div>
      {msg ? <p className="booking-activity-reschedule-msg">{msg}</p> : null}
    </div>
  );
}

export function CustomerActivityDrawer(props: {
  tenantId: string;
  email?: string | null;
  telegramId?: string | null;
  whatsappId?: string | null;
  open: boolean;
  onClose: () => void;
  onUnreadCount?: (n: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<CustomerActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);
  const unreadRef = useRef(props.onUnreadCount);
  unreadRef.current = props.onUnreadCount;

  /**
   * ⚠️ REÚNE SESIÓN, TELEGRAM Y `guestId`. Antes se pedía con `{ tenantId }` a secas: quien
   * abriera este panel con su cuenta desde otro dispositivo se acreditaba solo como invitado
   * de ese navegador, y desde que booking-service exige credencial eso es un error en pantalla.
   */
  const obtenerCredencial = useCustomerCredential();

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // Credencial de identidad (B2-1). Está cacheada en memoria por tenant, así que abrir
      // el panel no añade una llamada extra si la página ya la pidió.
      const cred = await obtenerCredencial(props.tenantId);
      const data = await fetchCustomerActivity({
        tenantId: props.tenantId,
        email: props.email,
        telegramId: props.telegramId,
        whatsappId: props.whatsappId,
        limit: 40,
        customerToken: cred.token,
      });
      setItems(data.items || []);
      setFetched(true);
      unreadRef.current?.(data.unreadCount ?? 0);
    } catch (e) {
      logger.warn('Customer activity fetch failed', e);
      setErr(t('booking.activity_error'));
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [props.email, props.telegramId, props.whatsappId, props.tenantId, t]);

  useEffect(() => {
    if (!props.open) return;
    void load();
  }, [props.open, load]);

  const canLoad = !!(props.email?.trim() || props.telegramId?.trim() || props.whatsappId?.trim());
  const emailTrim = (props.email || '').trim();

  return (
    <Drawer open={props.open} onClose={props.onClose} size="md" title={t('booking.activity_title')} ariaLabel={t('booking.activity_title')}>
      {!canLoad ? (
        <p className="booking-activity-msg">{t('booking.activity_need_identity')}</p>
      ) : loading && !fetched ? (
        <p className="booking-activity-msg">{t('booking.loading')}</p>
      ) : err ? (
        <p className="booking-activity-msg booking-activity-msg--error">{err}</p>
      ) : items.length === 0 ? (
        <p className="booking-activity-msg">{t('booking.activity_empty')}</p>
      ) : (
        <ul className="booking-activity-list">
          {items.map((item) => (
            <li key={item.id} className="booking-activity-item">
              <span className="booking-activity-item-summary">{item.summary}</span>
              <span className="booking-activity-item-meta">
                {formatWhen(item.createdAt, i18n.language)} · {item.eventType}
              </span>
              {item.eventType === 'booking.reschedule_proposed' && emailTrim.includes('@') ? (
                <ActivityRescheduleProposalBlock
                  tenantId={props.tenantId}
                  email={emailTrim}
                  item={item}
                  lng={i18n.language}
                  onRefresh={load}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}

export function CustomerActivityBellButton(props: {
  onClick: () => void;
  unreadCount?: number;
  label: string;
}) {
  const n = props.unreadCount && props.unreadCount > 0 ? Math.min(props.unreadCount, 99) : 0;
  return (
    <button
      type="button"
      className="booking-activity-bell-btn"
      onClick={props.onClick}
      aria-label={props.label}
      title={props.label}
    >
      <span className="booking-activity-bell" aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </span>
      {n > 0 ? <span className="booking-activity-badge">{n}</span> : null}
    </button>
  );
}
